import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Navigate } from '@sorare/routing';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import metadataImage from '@sorare/core/src/assets/logos/mlb/mlb.svg';
import { ANY_SPORT_HOME } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { MLBTeams } from '@sorare/core/src/lib/mlbTeams';

import { EarlyAccess } from 'components/landing/EarlyAccess';

const messages = defineMessages({
  metadatasTitle: {
    id: 'Landing.MLB.metadatasTitle',
    defaultMessage:
      'Play fantasy baseball with ownable MLB player cards - Sorare',
  },
  metadatasDescription: {
    id: 'Landing.MLB.metadatasDescription',
    defaultMessage:
      "Play Sorare's free fantasy baseball game. Collect, buy, sell & compete with ownable digital player cards from all 30 MLB teams to win cash, ETH, tickets, more",
  },
});

type Props = {
  isRoot?: boolean;
};
export const Home = ({ isRoot }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const track = useEvents();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: metadataImage,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      track('View Homepage Disconnected', {
        landing_variant: 'MLB',
      });
    }
  }, [isLoggedIn, track]);

  if (!currentUser) {
    return (
      <EarlyAccess
        leaderboards={[
          So5LeaderboardType.MLB_LIMITED_CHAMPION,
          So5LeaderboardType.MLB_UNIQUE_CHAMPION,
          So5LeaderboardType.MLB_RARE_CHAMPION,
        ]}
        leagues={MLBTeams.map(team => ({
          displayName: team.fullName,
          pictureSecondaryUrl: team.svgUrl,
        }))}
        showFAQ={!isRoot}
      />
    );
  }

  return (
    <Navigate
      to={generateSportContextPath(ANY_SPORT_HOME)}
      replace
      keepSearchParams
    />
  );
};

export default Home;
